import { useEffect, useRef, useCallback } from 'react'

/**
 * Returns the number of milliseconds from now until the next local midnight.
 */
const msUntilMidnight = (): number => {
  const now = new Date()
  const midnight = new Date(now)
  midnight.setHours(24, 0, 0, 0)
  return midnight.getTime() - now.getTime()
}

export interface UseMidnightRefreshOptions {
  /** When true, no timer is set */
  skip?: boolean
}

/**
 * Schedules a callback to fire at the next local midnight, then re-schedules
 * for each subsequent midnight so it keeps working across multiple day
 * boundaries without a page reload.
 *
 * - Only one `setTimeout` is active at a time (cleared before re-scheduling).
 * - The timer is cleaned up on unmount or when `callback`/`skip` changes.
 * - When `skip` is true no timer is set.
 */
export const useMidnightRefresh = (callback: () => void, skip = false): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const schedule = useCallback(() => {
    clear()
    timerRef.current = setTimeout(() => {
      callback()
      schedule()
    }, msUntilMidnight())
  }, [callback, clear])

  useEffect(() => {
    if (skip) {
      clear()
      return undefined
    }

    schedule()

    return clear
  }, [skip, schedule, clear])
}

export { msUntilMidnight }
